// THE ENVIRONMENT PROBE — the sky a metal reflects.
//
// The gold audit's strongest forensic finding about the reference footage is
// that its "expensive metal" look is not ray tracing and never was: it is an
// environment probe containing a genuine, unclipped HDR sun disc, which a
// low-roughness GGX lobe returns as a small bright sun-shaped highlight. It is
// the cheapest fidelity in the whole audit.
//
// THE PROBE THIS REPLACES WAS WRITTEN AND HAS NEVER FIRED ONCE. render/
// atmosphere.ts built a 64×32 float equirect and drew the sun with
//
//     const disc = cosSun > 0.9995 ? 260 : 0;
//
// cos 0.9995 is a cone of angular radius 1.81°, whose solid angle is 0.025% of
// the sphere. A 64×32 equirect has 2 048 texels, so the EXPECTED number that
// fall inside that cone is 0.5 — the disc is a coin flip against the sun's
// staging, and both of this game's stagings lose it. Measured, against the
// exact texel centres that file generates:
//
//     desert sun (0.82, 0.46, −0.28)   0 texels;  nearest is 2.75° off
//     fen sun    (bearing 218.5°, 13°)  0 texels;  nearest is 2.16° off
//
// and across 1 080 arbitrary stagings, 60.0% have no disc at all. Every "hero
// metal" in this client — the plaza's condenser dome, the barge's engine
// cowls, the waypost brass, the parked skiff, at envMapIntensity 6.5 to 9 —
// has been reflecting a sky with no sun in it, and every comment claiming a
// highlight was measured off the HALO term instead.
//
// A THRESHOLD IS THE WRONG PRIMITIVE, and no resolution fixes it: the sun's
// true angular radius is 0.27°, a probe texel here is 1.4°, so any threshold
// test is asking whether a point sample happens to land inside something five
// times smaller than the sampling grid. What a disc actually is, physically,
// is an IRRADIANCE — a fixed amount of energy arriving from a small solid
// angle — and energy is a thing you can rasterise exactly at any resolution:
// spread it over the texels nearest the sun with any kernel you like, then
// normalise by the solid angle that kernel actually covered. `sunIrradiance`
// then means the same thing at 64×32 and at 512×256, the disc cannot fall
// between texels at any staging, and the peak radiance comes out as hot as the
// resolution allows — which for a 256×128 probe is ~400× the sky, i.e. an
// unclipped sun in the strict HDR sense the audit asks for.
//
// WHY THIS FILE IS IN THE FORGE. Same reason the surface generators are: it is
// pure numerics with no three.js and no DOM, it is deterministic, and it is
// therefore testable — `probe.test.ts` asserts the disc's energy is invariant
// under resolution and staging, which is the exact property the old one
// silently lacked. The client evaluates it (8 ms of arithmetic, once per biome
// per scene, never per frame) and hands the result to three, which does the
// PMREM convolution on the GPU where it belongs.
//
// AND WHY IT IS NOT BAKED TO A FILE. It could be: 256×128 RGBE is 128 kB per
// biome. It would buy nothing. The output is a closed-form function of nine
// numbers this client already blends per frame, so shipping the rasterisation
// would cost ~750 kB of committed payload to avoid 8 ms of arithmetic, in a
// game whose pitch is click-a-link onboarding. Offline authoring is the point;
// offline STORAGE is only the point when the content cannot be recomputed, and
// a sky gradient can.

/** Linear RGB. Not sRGB — this is radiance, and it is allowed past 1. */
export interface ProbeColor {
  readonly r: number;
  readonly g: number;
  readonly b: number;
}

export interface ProbeSpec {
  /** Sky at the horizon, linear. */
  readonly horizon: ProbeColor;
  /** Sky at the zenith, linear. */
  readonly zenith: ProbeColor;
  /** What is below the horizon: the lit ground, seen by a downward normal. */
  readonly ground: ProbeColor;
  /** Unit vector toward the sun (or the moon). */
  readonly sun: readonly [number, number, number];
  /** The sun's own colour, linear and normalised — brightness rides below. */
  readonly sunColor: ProbeColor;
  /**
   * TOTAL irradiance the disc contributes, in linear units — the whole point
   * of the file. Resolution-independent by construction: the rasteriser
   * normalises by the solid angle its kernel actually covered, so this number
   * means the same thing at every probe size and every sun staging.
   */
  readonly sunIrradiance: number;
  /**
   * Angular radius of the drawn disc, radians. The real sun is 0.0047 (0.27°);
   * this is deliberately larger, because a probe texel is 0.024 rad and a
   * source smaller than the sampling grid buys nothing but aliasing. What it
   * costs is the highlight's size at roughness → 0, and at 0.02 rad that is
   * about a degree, which is a sun.
   */
  readonly discRadius: number;
  /** Halo terms: [weight, cosine power] pairs, added around the sun. */
  readonly halo: readonly (readonly [number, number])[];
  /** Where the horizon→zenith ramp starts and ends, in sin(elevation). */
  readonly rampLo: number;
  readonly rampHi: number;
  /**
   * A second, wide, dim source for what the sky itself does not explain: the
   * neon spill and window glow of a night city, which is most of what a brass
   * fitting reflects down there. `[weight, cosine power]` around −sun, i.e.
   * from below and behind. 0 disables it.
   */
  readonly glow?: readonly [number, number];
  /** Colour of that glow, linear. */
  readonly glowColor?: ProbeColor;
}

const smoothstep = (a: number, b: number, x: number): number => {
  const t = Math.min(1, Math.max(0, (x - a) / Math.max(1e-9, b - a)));
  return t * t * (3 - 2 * t);
};

/**
 * Rasterise a probe into an RGBA float equirect, top-row-first.
 *
 * THE LAYOUT IS THREE'S, EXACTLY. `equirectUv()` in three's shader library is
 * `u = atan2(z, x)/2π + 0.5`, `v = asin(y)/π + 0.5`, and a `DataTexture` with
 * `flipY = false` puts v = 0 in row 0 — so row 0 is straight DOWN and the
 * final row is straight up. Getting this wrong renders a sky that is upside
 * down only in reflections, which is the kind of bug that survives a wave.
 */
export function rasteriseProbe(spec: ProbeSpec, width: number, height: number): Float32Array {
  const data = new Float32Array(width * height * 4);
  const [sx, sy, sz] = spec.sun;

  // PASS ONE: the sky, the ground, the halo — everything whose value at a
  // texel depends only on that texel — plus the disc's unnormalised kernel and
  // the solid angle it covers. One loop, because the trigonometry is shared.
  const disc = new Float32Array(width * height);
  let discSolidAngle = 0;
  // A texel's angular width at the equator. The kernel is never allowed
  // narrower than 1.5 of these: below that it can slip between texel centres,
  // which is the entire failure this file exists to fix.
  const texelAngle = Math.PI / height;
  const radius = Math.max(spec.discRadius, texelAngle * 1.5);
  const dOmega = ((2 * Math.PI) / width) * (Math.PI / height);

  for (let j = 0; j < height; j++) {
    const v = (j + 0.5) / height;
    const elev = (v - 0.5) * Math.PI;
    const cosE = Math.cos(elev);
    const sinE = Math.sin(elev);
    for (let i = 0; i < width; i++) {
      const phi = ((i + 0.5) / width - 0.5) * Math.PI * 2;
      const dx = Math.cos(phi) * cosE;
      const dy = sinE;
      const dz = Math.sin(phi) * cosE;

      let r: number;
      let g: number;
      let b: number;
      if (dy < 0) {
        // Below the horizon: the lit ground, fading as it turns under.
        const k = 1 + dy * 0.55;
        r = spec.ground.r * k;
        g = spec.ground.g * k;
        b = spec.ground.b * k;
      } else {
        const t = smoothstep(spec.rampLo, spec.rampHi, dy);
        r = spec.horizon.r + (spec.zenith.r - spec.horizon.r) * t;
        g = spec.horizon.g + (spec.zenith.g - spec.horizon.g) * t;
        b = spec.horizon.b + (spec.zenith.b - spec.horizon.b) * t;
      }

      const cosSun = Math.max(0, dx * sx + dy * sy + dz * sz);
      let halo = 0;
      for (const [weight, power] of spec.halo) halo += weight * cosSun ** power;
      r += spec.sunColor.r * halo;
      g += spec.sunColor.g * halo;
      b += spec.sunColor.b * halo;

      if (spec.glow && spec.glowColor) {
        // From below and behind: a city's own light, coming back up.
        const down = Math.max(0, -dy);
        const k = spec.glow[0] * down ** spec.glow[1];
        r += spec.glowColor.r * k;
        g += spec.glowColor.g * k;
        b += spec.glowColor.b * k;
      }

      const o = (j * width + i) * 4;
      data[o] = r;
      data[o + 1] = g;
      data[o + 2] = b;
      data[o + 3] = 1;

      // The disc's kernel. Smooth to the edge so a sun crossing a texel
      // boundary does not step, and clamped to ±1 before acos because a dot
      // product of unit vectors in floating point is not.
      const angle = Math.acos(Math.min(1, Math.max(-1, dx * sx + dy * sy + dz * sz)));
      if (angle < radius) {
        const k = 1 - smoothstep(radius * 0.35, radius, angle);
        const idx = j * width + i;
        disc[idx] = k;
        discSolidAngle += k * dOmega * cosE;
      }
    }
  }

  // PASS TWO: normalise the disc so its integral over the sphere is exactly
  // `sunIrradiance`, whatever the resolution and wherever the sun is staged.
  // `discSolidAngle` cannot be zero — the kernel is at least 1.5 texels wide
  // and every direction on the sphere has a texel within one — but a guard
  // costs nothing and a divide by zero costs a frame of NaN.
  if (discSolidAngle > 1e-12) {
    const gain = spec.sunIrradiance / discSolidAngle;
    for (let idx = 0; idx < disc.length; idx++) {
      const k = disc[idx] ?? 0;
      if (k <= 0) continue;
      const o = idx * 4;
      data[o] = (data[o] ?? 0) + spec.sunColor.r * k * gain;
      data[o + 1] = (data[o + 1] ?? 0) + spec.sunColor.g * k * gain;
      data[o + 2] = (data[o + 2] ?? 0) + spec.sunColor.b * k * gain;
    }
  }

  return data;
}

/**
 * The disc's total irradiance, integrated back off a finished probe against a
 * reference sky rasterised without it.
 *
 * Exists for the gate. The claim this file makes — that the sun's energy is
 * invariant under resolution and staging — is only worth making if something
 * checks it, and the only honest check is to measure the shipped raster.
 */
export function discIrradiance(spec: ProbeSpec, width: number, height: number): number {
  const withSun = rasteriseProbe(spec, width, height);
  const without = rasteriseProbe({ ...spec, sunIrradiance: 0 }, width, height);
  let sum = 0;
  const dOmega = ((2 * Math.PI) / width) * (Math.PI / height);
  for (let j = 0; j < height; j++) {
    const cosE = Math.cos(((j + 0.5) / height - 0.5) * Math.PI);
    for (let i = 0; i < width; i++) {
      const o = (j * width + i) * 4;
      // Luminance of the difference, weighted by the texel's solid angle.
      const dr = (withSun[o] ?? 0) - (without[o] ?? 0);
      const dg = (withSun[o + 1] ?? 0) - (without[o + 1] ?? 0);
      const db = (withSun[o + 2] ?? 0) - (without[o + 2] ?? 0);
      sum += (0.2126 * dr + 0.7152 * dg + 0.0722 * db) * dOmega * cosE;
    }
  }
  return sum;
}

/** Peak radiance anywhere in a probe — how unclipped the sun actually is. */
export function peakRadiance(data: Float32Array): number {
  let peak = 0;
  for (let i = 0; i < data.length; i += 4) {
    const y = 0.2126 * (data[i] ?? 0) + 0.7152 * (data[i + 1] ?? 0) + 0.0722 * (data[i + 2] ?? 0);
    if (y > peak) peak = y;
  }
  return peak;
}
